import React from 'react';
import { Compass, Package, HardHat, Grid3x3, CheckCircle, RefreshCw, Zap, BarChart3, Wrench, Monitor } from 'lucide-react';
import { activitiesData } from '../mock/mockData';

const iconMap = {
  Compass,
  Package,
  HardHat,
  Grid3x3,
  CheckCircle,
  RefreshCw,
  Zap,
  BarChart3,
  Wrench,
  Monitor
};

const Activities = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#081221] to-[#0d63ce]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">
            Le Nostre Attività
          </h1>
          <p className="text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Soluzioni complete per l'intero ciclo di vita degli impianti fotovoltaici utility-scale
          </p>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {activitiesData.map((activity) => {
              const IconComponent = iconMap[activity.icon];
              return (
                <div
                  key={activity.id}
                  className="group bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#081221] mb-4">
                    {activity.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{activity.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EPC Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Processo EPC Full Scope
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un approccio integrato dalla progettazione alla messa in servizio
            </p>
          </div>

          <div className="relative">
            {/* Process Flow */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#0d63ce] to-[#18a06b]" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[
                { title: 'Design', icon: Compass, description: 'Progettazione dettagliata' },
                { title: 'Procurement', icon: Package, description: 'Approvvigionamento' },
                { title: 'Construction', icon: HardHat, description: 'Costruzione' },
                { title: 'Testing', icon: CheckCircle, description: 'Collaudo' },
                { title: 'Commissioning', icon: Zap, description: 'Messa in servizio' }
              ].map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="relative mb-6">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-full flex items-center justify-center shadow-xl">
                        <IconComponent className="text-white" size={36} />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-[#0d63ce] font-bold text-[#0d63ce]">
                        {index + 1}
                      </div>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#081221] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Construction Excellence */}
      <section className="py-24 bg-[#081221] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="/construction_tep.jpg"
                alt="Eccellenza nella Realizzazione"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="font-serif text-5xl font-bold mb-6">Eccellenza nella Realizzazione</h2>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                Gestiamo l'intera filiera di costruzione con team qualificati e processi certificati,
                garantendo qualità, sicurezza e rispetto dei tempi.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '600+', label: 'MWp installati' },
                  { value: '20+', label: 'Anni di esperienza' },
                  { value: '100%', label: 'Safety standard' },
                  { value: '50+', label: 'Progetti completati' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-[#18a06b] mb-2">{stat.value}</div>
                    <div className="text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revamping & Repowering */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
                Revamping & Repowering
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Miglioriamo le performance di impianti esistenti attraverso interventi mirati di
                ammodernamento e sostituzione di componenti obsoleti.
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: 'Revamping',
                    description:
                      'Ottimizzazione di impianti esistenti per migliorare efficienza e produttività'
                  },
                  {
                    title: 'Repowering',
                    description:
                      'Sostituzione di moduli e inverter con tecnologie di ultima generazione'
                  },
                  {
                    title: 'Upgrade BoP',
                    description: 'Ammodernamento di infrastrutture elettriche e sistemi di controllo'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-lg flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-serif text-xl font-bold text-[#081221] mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="/photo5.jpeg"
                alt="Revamping"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Activities;
